/**
 * Converts one-time cart lines to subscriptions IN PLACE.
 *
 * /cart/change.js accepts a selling_plan, so the subscribe band's button no
 * longer detours through the product page. The band itself lives inside the
 * re-rendered cart contents (.drawer__inner / .js-contents), where an inline
 * <script> would never execute after an innerHTML swap — so everything here
 * hangs off one delegated document listener, wired once per page load.
 *
 * ── LINE KEYS ARE QUICKSAND ─────────────────────────────────────────────────
 * Two verified behaviours shape this file:
 *   1. Attaching a plan CHANGES the line's key (one-time and subscription are
 *      separate lines).
 *   2. A key can change WITHOUT the line being touched: converting one of two
 *      one-time formulas drops the automatic 2-jar bundle discount, which
 *      re-keys the sibling line — posting the sibling's pre-rendered key then
 *      400s. So no key is trusted across a mutation: every move re-resolves
 *      its line (by variant, and plan when it should hold one) from the
 *      freshest cart state right before its POST.
 *
 * The same re-resolution runs once more against the live cart BEFORE anything
 * is posted: on /cart the page and drawer surfaces do not re-render each
 * other after Dawn's own quantity edits, so a band can be stale. A mismatch
 * (missing line, changed quantity) converts the click into a silent section
 * refresh — both bands recompute to the truth instead of executing an
 * outdated ladder.
 *
 * If conversion landed on a pre-existing subscription line of the same
 * variant the quantities merged; undo could not restore the world, so the
 * strip is not offered (rollback peels merged jars back off, best-effort).
 *
 * ── STEPPERS ON SUBSCRIPTION LINES ──────────────────────────────────────────
 * A quantity edit on a plan line means "leave the plan at this new quantity".
 * Dawn's stepper would otherwise keep the plan while changing the count,
 * minting tiers the store never sells (2 jars at the 3-month discount, 1 jar
 * every 6 months at 30%). The cart templates stamp data-sb-plan-line-key on
 * plan lines' quantity inputs; a capture-phase change listener takes those
 * edits away from Dawn entirely and posts the new quantity WITH
 * selling_plan: null — the line reverts to one-time and the re-rendered
 * subscribe band immediately offers the correct tier for the new count.
 */
(() => {
  if (window.sbCartSubscribeWired) return;
  window.sbCartSubscribeWired = true;

  let busy = false;

  document.addEventListener('click', (event) => {
    const convertButton = event.target.closest('[data-sb-sub-convert]');
    if (convertButton) {
      onConvert(convertButton);
      return;
    }
    const undoButton = event.target.closest('[data-sb-sub-undo]');
    if (undoButton) onUndo(undoButton);
  });

  // Capture phase, so neither Dawn's CartItems change subscriber nor the
  // quantity-input element's own validation ever sees a plan-line edit —
  // one choke point that covers minus, plus, and typed quantities alike.
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input || !input.dataset || !input.dataset.sbPlanLineKey) return;
      event.stopPropagation();
      onPlanQuantityEdit(input);
    },
    true
  );

  function sectionTargets() {
    const targets = [];
    if (document.querySelector('#CartDrawer .drawer__inner')) {
      targets.push({
        section: 'cart-drawer',
        node: () => document.querySelector('#CartDrawer .drawer__inner'),
        selector: '.drawer__inner',
      });
    }
    const items = document.getElementById('main-cart-items');
    if (items && items.dataset.id) {
      targets.push({
        section: items.dataset.id,
        node: () => items.querySelector('.js-contents'),
        selector: '.js-contents',
      });
    }
    const footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) {
      targets.push({
        section: footer.dataset.id,
        node: () => footer.querySelector('.js-contents'),
        selector: '.js-contents',
      });
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      targets.push({
        section: 'cart-icon-bubble',
        node: () => bubble.querySelector('.shopify-section') || bubble,
        selector: '.shopify-section',
      });
    }
    return targets;
  }

  function sectionNames() {
    return sectionTargets().map((target) => target.section);
  }

  function applySections(sections) {
    if (!sections) return;
    sectionTargets().forEach((target) => {
      const html = sections[target.section];
      const node = target.node();
      if (!html || !node) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      if (fresh) node.innerHTML = fresh.innerHTML;
    });
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      bubble.classList.toggle('site-actions__cart--empty', !bubble.querySelector('.site-actions__cart-count'));
    }
  }

  function getCart() {
    return fetch(`${routes.cart_url || '/cart'}.js`).then((response) => response.json());
  }

  function change(payload, attempt = 0) {
    const config = fetchConfig();
    config.body = JSON.stringify(payload);
    return fetch(routes.cart_change_url, config).then((response) => {
      // Shopify throttles cart WRITES per IP in bursts (429, HTML body).
      // A short backoff absorbs the burst instead of surfacing the generic
      // cart error for a wobble that clears itself in a second or two.
      if (response.status === 429 && attempt < 2) {
        return new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1))).then(() =>
          change(payload, attempt + 1)
        );
      }
      return response.json().then((state) => {
        if (!response.ok || state.status) {
          const error = new Error('Cart change failed');
          // Only 422s carry shopper-appropriate text (availability copy). A
          // 400 bad_request says "no valid id or line parameter" — developer
          // jargon, English-only — so those fall back to cartStrings.error.
          const detail = state.description || state.message;
          if (response.status === 422 && typeof detail === 'string' && detail) error.userMessage = detail;
          throw error;
        }
        return state;
      });
    });
  }

  function addLine(variantId, quantity) {
    const config = fetchConfig();
    config.body = JSON.stringify({ items: [{ id: variantId, quantity }] });
    return fetch(routes.cart_add_url, config).then((response) => response.json());
  }

  // A line that should NOT hold a plan yet: the conversion source.
  function resolveOneTime(items, move, used) {
    return (items || []).find(
      (item) => !used.has(item.key) && item.variant_id === move.variant && !item.selling_plan_allocation
    );
  }

  // A line that SHOULD hold the move's plan: the conversion result.
  function resolveSub(items, move, used) {
    return (items || []).find(
      (item) =>
        !used.has(item.key) &&
        item.variant_id === move.variant &&
        item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan.id === move.plan
    );
  }

  // Section Rendering API GET, deliberately without an Accept header: asking
  // for application/json makes /?sections= return the cart object instead.
  function refresh() {
    const names = sectionNames();
    if (!names.length) return Promise.resolve();
    return fetch(`${window.location.pathname}?sections=${names.join(',')}`)
      .then((response) => response.json())
      .then((sections) => applySections(sections))
      .catch(() => {});
  }

  // The drawer exists on every page, but on /cart it sits closed and
  // invisible — feedback has to land on the surface the shopper acted on.
  function showError(inDrawer, message) {
    const drawerNode = document.getElementById('CartDrawer-CartErrors');
    const pageNode = document.getElementById('cart-errors');
    const node = (inDrawer ? drawerNode : pageNode) || drawerNode || pageNode;
    if (!node) return;
    node.textContent = message || (window.cartStrings && window.cartStrings.error) || '';
  }

  function announce(inDrawer, text) {
    const drawerNode = document.getElementById('CartDrawer-LiveRegionText');
    const pageNode = document.getElementById('cart-live-region-text');
    const region = (inDrawer ? drawerNode : pageNode) || drawerNode || pageNode;
    if (!region || !text) return;
    region.setAttribute('aria-hidden', 'false');
    region.textContent = text;
    setTimeout(() => region.setAttribute('aria-hidden', 'true'), 1000);
  }

  // Published as source 'cart-items' on purpose: CartItems' subscriber skips
  // that source, and this script has already re-rendered every section a
  // refetch would rebuild — an onCartUpdate() here would only race the DOM
  // and wipe the freshly inserted undo strip. The first-add sheet listens
  // solely for 'product-form', so it is unaffected either way.
  // NOTE: PUB_SUB_EVENTS is a top-level const (constants.js) — a lexical
  // global, NOT a window property — so it must be probed with typeof.
  function publishUpdate(state) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
    }
  }

  function setBusy(button, label) {
    button.disabled = true;
    button.classList.add('is-busy');
    if (label) button.textContent = label;
  }

  function setIdle(button, label) {
    if (!button) return;
    button.disabled = false;
    button.classList.remove('is-busy');
    if (label) button.textContent = label;
  }

  function unbusyUndoStrips() {
    document.querySelectorAll('.sb-subband-undo').forEach((node) => node.classList.remove('is-busy'));
    document.querySelectorAll('.sb-subband-undo__btn').forEach((node) => {
      node.disabled = false;
    });
  }

  function insertUndo(undoMoves, strings) {
    document.querySelectorAll('.sb-subband-undo').forEach((node) => node.remove());
    const spots = [
      {
        context: 'drawer',
        anchor: () => document.querySelector('.sb-acct-soon--drawer'),
        container: () => document.querySelector('#CartDrawer cart-drawer-items'),
      },
      {
        context: 'page',
        anchor: () => document.querySelector('.sb-acct-soon--page'),
        container: () => document.querySelector('#main-cart-items .js-contents'),
      },
    ];
    spots.forEach((spot) => {
      const anchor = spot.anchor();
      const container = anchor ? null : spot.container();
      if (!anchor && !container) return;
      const strip = document.createElement('div');
      strip.className = `sb-subband-undo sb-subband-undo--${spot.context}`;
      strip.setAttribute('role', 'status');
      strip.dataset.sbSubUndoMoves = JSON.stringify(undoMoves);
      strip.dataset.sbSubUndoneNote = strings.undone;
      const message = document.createElement('span');
      message.className = 'sb-subband-undo__msg';
      message.textContent = strings.done;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sb-subband-undo__btn';
      button.setAttribute('data-sb-sub-undo', '');
      button.textContent = strings.undo;
      strip.append(message, button);
      if (anchor) {
        anchor.before(strip);
      } else {
        container.append(strip);
      }
    });
  }

  async function rollback(applied) {
    if (!applied.length) return;
    let cart;
    try {
      cart = await getCart();
    } catch (error) {
      return;
    }
    const used = new Set();
    for (const move of applied) {
      const line = resolveSub(cart.items, move, used);
      if (!line) continue;
      used.add(line.key);
      try {
        if (line.quantity === move.qty) {
          await change({ id: line.key, quantity: move.back, selling_plan: null });
        } else if (line.quantity > move.qty) {
          // The conversion merged into a pre-existing subscription line of
          // the same variant: peel our jars back off it (the plan stays when
          // selling_plan is omitted), then restore the one-time line.
          await change({ id: line.key, quantity: line.quantity - move.qty });
          await addLine(move.variant, move.back);
        }
      } catch (error) {
        // Best effort — the section refresh after rollback shows the truth.
      }
    }
  }

  // A quantity edit on a subscription line: revert the line to one-time at
  // the new count (or remove it at 0). The re-render brings the subscribe
  // band back with the right tier for the new quantity, so the way back into
  // a coherent subscription is always on screen.
  async function onPlanQuantityEdit(input) {
    const rendered = parseInt(input.getAttribute('value'), 10);
    let next = parseInt(input.value, 10);
    if (Number.isNaN(next) || next < 0) next = rendered;
    if (busy || Number.isNaN(rendered)) {
      input.value = rendered;
      return;
    }
    if (next === rendered) return;

    busy = true;
    const inDrawer = !!input.closest('cart-drawer');
    const undoneNote = input.dataset.sbPlanUndone || '';
    input.disabled = true;

    // Same stale-surface guard as onConvert: trust only the live cart.
    let line = null;
    try {
      const items = (await getCart()).items || [];
      line = items.find((item) => item.key === input.dataset.sbPlanLineKey);
    } catch (error) {
      input.disabled = false;
      input.value = rendered;
      busy = false;
      return;
    }
    if (!line || line.quantity !== rendered) {
      await refresh();
      busy = false;
      return;
    }

    try {
      const payload = {
        id: line.key,
        quantity: next,
        sections: sectionNames(),
        sections_url: window.location.pathname,
      };
      if (next > 0) payload.selling_plan = null;
      const state = await change(payload);
      // Any undo strip from an earlier conversion is stale now.
      document.querySelectorAll('.sb-subband-undo').forEach((node) => node.remove());
      applySections(state.sections);
      if (next > 0) announce(inDrawer, undoneNote);
      publishUpdate(state);
    } catch (error) {
      await refresh();
      showError(inDrawer, error && error.userMessage);
    }
    busy = false;
  }

  async function onConvert(button) {
    if (busy) return;
    const band = button.closest('[data-sb-sub-band]');
    if (!band) return;
    let moves;
    try {
      moves = JSON.parse(band.dataset.sbSubMoves);
    } catch (error) {
      return;
    }
    if (!Array.isArray(moves) || !moves.length) return;

    busy = true;
    const inDrawer = !!button.closest('cart-drawer');
    const originalLabel = button.textContent;
    const strings = {
      done: band.dataset.sbSubDone || '',
      undo: band.dataset.sbSubUndoLabel || '',
      undone: band.dataset.sbSubUndone || '',
      working: band.dataset.sbSubWorking || '',
    };
    setBusy(button, strings.working);

    // Validate against the live cart before touching anything: a band on the
    // other surface (or a cached page) may be stale. Any mismatch turns the
    // click into a plain re-render — no conversion, no error.
    let liveItems;
    try {
      liveItems = (await getCart()).items || [];
    } catch (error) {
      setIdle(button, originalLabel);
      busy = false;
      return;
    }
    const usedLive = new Set();
    for (const move of moves) {
      const line = resolveOneTime(liveItems, move, usedLive);
      if (!line || line.quantity !== move.back) {
        await refresh();
        // refresh() swallows its failures, so the fresh band may never have
        // arrived — hand the (possibly still-attached) button back either way.
        setIdle(button, originalLabel);
        busy = false;
        return;
      }
      usedLive.add(line.key);
      move.key = line.key;
    }

    const applied = [];
    let state = null;
    try {
      for (let index = 0; index < moves.length; index += 1) {
        const move = moves[index];
        if (state) {
          // An earlier move can have re-keyed this line (discount churn).
          const line = resolveOneTime(state.items, move, new Set());
          if (!line || line.quantity !== move.back) throw new Error('Line changed during conversion');
          move.key = line.key;
        }
        const payload = { id: move.key, quantity: move.qty, selling_plan: move.plan };
        if (index === moves.length - 1) {
          payload.sections = sectionNames();
          payload.sections_url = window.location.pathname;
        }
        state = await change(payload);
        applied.push(move);
      }
    } catch (error) {
      await rollback(applied);
      await refresh();
      showError(inDrawer, error && error.userMessage);
      setIdle(button, originalLabel);
      busy = false;
      return;
    }

    // Re-find each converted line under its new key for the undo payload.
    const usedSub = new Set();
    const undoMoves = [];
    let merged = false;
    applied.forEach((move) => {
      const line = resolveSub(state.items, move, usedSub);
      if (!line || line.quantity !== move.qty) {
        merged = true;
        return;
      }
      usedSub.add(line.key);
      undoMoves.push({ key: line.key, variant: move.variant, plan: move.plan, qty: move.qty, back: move.back });
    });

    applySections(state.sections);
    if (!merged && undoMoves.length === applied.length) insertUndo(undoMoves, strings);
    announce(inDrawer, strings.done);
    publishUpdate(state);
    busy = false;
  }

  async function onUndo(button) {
    if (busy) return;
    const strip = button.closest('.sb-subband-undo');
    if (!strip) return;
    let moves;
    try {
      moves = JSON.parse(strip.dataset.sbSubUndoMoves);
    } catch (error) {
      return;
    }
    if (!Array.isArray(moves) || !moves.length) return;

    busy = true;
    const inDrawer = !!button.closest('cart-drawer');
    button.disabled = true;
    document.querySelectorAll('.sb-subband-undo').forEach((node) => node.classList.add('is-busy'));

    // The strip's stored keys are hints, not truth — re-resolve every line
    // from the live cart. A move is dropped when its line no longer exists
    // OR its quantity moved on (the shopper edited the subscription after
    // converting — undo only reverts the exact state it created, the same
    // strictness onConvert applies to its own inputs).
    let liveItems;
    try {
      liveItems = (await getCart()).items || [];
    } catch (error) {
      unbusyUndoStrips();
      busy = false;
      return;
    }
    const used = new Set();
    const live = [];
    for (const move of moves) {
      const line = resolveSub(liveItems, move, used);
      if (!line || line.quantity !== move.qty) continue;
      used.add(line.key);
      live.push({ key: line.key, variant: move.variant, plan: move.plan, qty: move.qty, back: move.back });
    }
    if (!live.length) {
      document.querySelectorAll('.sb-subband-undo').forEach((node) => node.remove());
      await refresh();
      busy = false;
      return;
    }

    let state = null;
    try {
      for (let index = 0; index < live.length; index += 1) {
        const move = live[index];
        if (state) {
          const line = resolveSub(state.items, move, new Set());
          if (!line || line.quantity !== move.qty) continue;
          move.key = line.key;
        }
        const payload = { id: move.key, quantity: move.back, selling_plan: null };
        if (index === live.length - 1) {
          payload.sections = sectionNames();
          payload.sections_url = window.location.pathname;
        }
        state = await change(payload);
      }
    } catch (error) {
      await refresh();
      showError(inDrawer, error && error.userMessage);
      unbusyUndoStrips();
      busy = false;
      return;
    }

    const undoneNote = strip.dataset.sbSubUndoneNote || '';
    document.querySelectorAll('.sb-subband-undo').forEach((node) => node.remove());
    if (state && state.sections) {
      applySections(state.sections);
    } else {
      // The last move's line vanished mid-sequence, so no sections came back.
      await refresh();
    }
    announce(inDrawer, undoneNote);
    publishUpdate(state);
    busy = false;
  }
})();
